export function toLocalDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseLocalDateString(dateString: string): Date | null {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function getLastDayOfCurrentMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
}

export function getFirstDayOfCurrentMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

/** Длина диапазона в днях (включительно) */
function getRangeLengthDays(dateStart: string, dateEnd: string): number {
  const start = parseLocalDateString(dateStart);
  const end = parseLocalDateString(dateEnd);
  if (!start || !end) return 0;
  const ms = end.getTime() - start.getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
}

/** Полный месяц: dateStart = 1-е число, dateEnd = последний день того же месяца */
function isFullMonth(start: Date, end: Date): boolean {
  return (
    start.getDate() === 1 &&
    end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate() &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  );
}

/** Полный квартал: 01.01-31.03, 01.04-30.06, 01.07-30.09, 01.10-31.12 */
function isFullQuarter(start: Date, end: Date): boolean {
  if (start.getDate() !== 1) return false;
  const qStartMonths = [0, 3, 6, 9]; // первый месяц квартала
  if (!qStartMonths.includes(start.getMonth())) return false;
  const expectedEndMonth = start.getMonth() + 2;
  const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  return (
    end.getMonth() === expectedEndMonth &&
    end.getDate() === lastDay &&
    start.getFullYear() === end.getFullYear()
  );
}

/** Полный год: 01.01 - 31.12 */
function isFullYear(start: Date, end: Date): boolean {
  return (
    start.getMonth() === 0 &&
    start.getDate() === 1 &&
    end.getMonth() === 11 &&
    end.getDate() === 31 &&
    start.getFullYear() === end.getFullYear()
  );
}

/** Прошлый период: месяц → предыдущий месяц, квартал → предыдущий квартал, год → предыдущий год, иначе по дням */
export function getPreviousRangeDates(dateStart: string, dateEnd: string): { dateStart: string; dateEnd: string } | null {
  const start = parseLocalDateString(dateStart);
  const end = parseLocalDateString(dateEnd);
  if (!start || !end) return null;

  if (isFullMonth(start, end)) {
    const prevStart = new Date(start.getFullYear(), start.getMonth() - 1, 1);
    const prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0);
    return { dateStart: toLocalDateString(prevStart), dateEnd: toLocalDateString(prevEnd) };
  }
  if (isFullQuarter(start, end)) {
    const prevStart = new Date(start.getFullYear(), start.getMonth() - 3, 1);
    const prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 3, 0);
    return { dateStart: toLocalDateString(prevStart), dateEnd: toLocalDateString(prevEnd) };
  }
  if (isFullYear(start, end)) {
    const prevStart = new Date(start.getFullYear() - 1, 0, 1);
    const prevEnd = new Date(start.getFullYear() - 1, 11, 31);
    return { dateStart: toLocalDateString(prevStart), dateEnd: toLocalDateString(prevEnd) };
  }

  const days = getRangeLengthDays(dateStart, dateEnd);
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - days + 1);
  return {
    dateStart: toLocalDateString(prevStart),
    dateEnd: toLocalDateString(prevEnd),
  };
}

/** Следующий период: месяц → следующий месяц, квартал → следующий квартал, год → следующий год, иначе по дням */
export function getNextRangeDates(dateStart: string, dateEnd: string): { dateStart: string; dateEnd: string } | null {
  const start = parseLocalDateString(dateStart);
  const end = parseLocalDateString(dateEnd);
  if (!start || !end) return null;

  if (isFullMonth(start, end)) {
    const nextStart = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const nextEnd = new Date(nextStart.getFullYear(), nextStart.getMonth() + 1, 0);
    return { dateStart: toLocalDateString(nextStart), dateEnd: toLocalDateString(nextEnd) };
  }
  if (isFullQuarter(start, end)) {
    const nextStart = new Date(start.getFullYear(), start.getMonth() + 3, 1);
    const nextEnd = new Date(nextStart.getFullYear(), nextStart.getMonth() + 3, 0);
    return { dateStart: toLocalDateString(nextStart), dateEnd: toLocalDateString(nextEnd) };
  }
  if (isFullYear(start, end)) {
    const nextStart = new Date(start.getFullYear() + 1, 0, 1);
    const nextEnd = new Date(start.getFullYear() + 1, 11, 31);
    return { dateStart: toLocalDateString(nextStart), dateEnd: toLocalDateString(nextEnd) };
  }

  const days = getRangeLengthDays(dateStart, dateEnd);
  const nextStart = new Date(end);
  nextStart.setDate(nextStart.getDate() + 1);
  const nextEnd = new Date(nextStart);
  nextEnd.setDate(nextEnd.getDate() + days - 1);
  return {
    dateStart: toLocalDateString(nextStart),
    dateEnd: toLocalDateString(nextEnd),
  };
}/** Тот же календарный диапазон в прошлом году */
export function getSameRangeLastYearDates(dateStart: string, dateEnd: string): { dateStart: string; dateEnd: string } | null {
  const start = parseLocalDateString(dateStart);
  const end = parseLocalDateString(dateEnd);
  if (!start || !end) return null;
  const prevStart = new Date(start);
  prevStart.setFullYear(prevStart.getFullYear() - 1);
  const prevEnd = new Date(end);
  prevEnd.setFullYear(prevEnd.getFullYear() - 1);
  return {
    dateStart: toLocalDateString(prevStart),
    dateEnd: toLocalDateString(prevEnd),
  };
}
